import {
  Badge,
  Button,
  Card,
  Checkbox,
  Input,
  Text,
  Textarea,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import {
  Add24Regular,
  ArrowLeft24Regular,
  ArrowRight24Regular,
  Delete24Regular,
  DocumentTable24Regular,
} from "@fluentui/react-icons";
import React, { useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { Page } from "../components/App";
import { DEFAULT_FIELDS, FIELD_CATEGORIES } from "../constants/fields";
import { useJob } from "../context/JobContext";
import { getAccessToken } from "../services/authService";
import {
  fetchFieldProfile,
  normaliseFieldProfile,
  UiFieldCategories,
} from "../services/fieldProfileService";
import { taskPaneBackground } from "../theme";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalM),
    boxSizing: "border-box",
    height: "88vh",
    ...taskPaneBackground,
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
    overflow: "hidden",
  },
  headerCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalXL),
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    display: "flex",
    flexDirection: "column",
    // Keep header compact: the default <h1>/<p> margins can create a large visual gap.
    ...shorthands.gap("2px"),
    flexShrink: 0,
  },
  headerTitle: {
    // `Text as="h1"` renders a real <h1> which has default browser margins unless reset.
    margin: 0,
  },
  headerSubtitle: {
    // `Text as="p"` renders a real <p> which has default browser margins unless reset.
    margin: 0,
    color: tokens.colorNeutralForeground3,
  },
  headerEyebrow: {
    color: "#0f6cbd",
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: tokens.spacingVerticalXXS,
  },
  mainCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.padding(tokens.spacingVerticalM),
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalM),
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
  },
  sidebar: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalXS),
    width: "100%",
    flexShrink: 0,
  },
  selectionWorkspace: {
    display: "grid",
    gridTemplateColumns: "minmax(140px, 180px) minmax(0, 1fr)",
    alignItems: "start",
    ...shorthands.gap(tokens.spacingHorizontalM),
    flexShrink: 0,
    "@media (max-width: 380px)": {
      gridTemplateColumns: "minmax(105px, 36%) minmax(0, 1fr)",
      ...shorthands.gap(tokens.spacingHorizontalS),
    },
  },
  categoryBtn: {
    width: "100%",
    justifyContent: "flex-start",
    ...shorthands.borderRadius("8px"),
  },
  categoryLabel: {
    display: "block",
    whiteSpace: "normal",
    textAlign: "left",
  },
  selectionSummary: {
    display: "flex",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "space-between",
    ...shorthands.gap(tokens.spacingHorizontalS),
    color: "#123b63",
    backgroundColor: "#eff6ff",
    ...shorthands.border("1px", "solid", "#bfdbfe"),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
  },
  summaryIdentity: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
    minWidth: 0,
  },
  summaryIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    flexShrink: 0,
    color: "#ffffff",
    backgroundColor: "#0f6cbd",
    ...shorthands.borderRadius("9px"),
  },
  mainContent: {
    flexShrink: 0,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  actionsBar: {
    display: "flex",
    flexShrink: 0,
    ...shorthands.gap(tokens.spacingHorizontalXS),
    flexWrap: "wrap",
    backgroundColor: "#f8fafc",
    ...shorthands.border("1px", "solid", "#e2e8f0"),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding(tokens.spacingVerticalS),
  },
  contentScroll: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalM),
    paddingRight: tokens.spacingHorizontalXXS,
    paddingBottom: tokens.spacingVerticalS,
  },
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    ...shorthands.gap(tokens.spacingHorizontalS),
    "@media (max-width: 600px)": {
      gridTemplateColumns: "minmax(0, 1fr)",
    },
  },
  fieldOption: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: "40px",
    backgroundColor: "#f8fafc",
    ...shorthands.border("1px", "solid", "#e2e8f0"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
  },
  categoryHeading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    ...shorthands.gap(tokens.spacingHorizontalS),
    marginBottom: tokens.spacingVerticalS,
  },
  customBuilder: {
    backgroundColor: "#eff6ff",
    ...shorthands.border("1px", "solid", "#bfdbfe"),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding(tokens.spacingVerticalM),
    marginBottom: tokens.spacingVerticalM,
  },
  sectionTitle: {
    display: "block",
    margin: 0,
  },
  sectionDescription: {
    display: "block",
    marginTop: tokens.spacingVerticalXXS,
    marginBottom: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground3,
  },
  customFieldInput: {
    display: "flex",
    ...shorthands.gap(tokens.spacingHorizontalXS),
  },
  ruleSection: {
    backgroundColor: "#f8fafc",
    ...shorthands.border("1px", "solid", "#e2e8f0"),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding(tokens.spacingVerticalM),
    marginTop: tokens.spacingVerticalM,
  },
  footerCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalXL),
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    flexShrink: 0,
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  footerActions: {
    display: "flex",
    ...shorthands.gap(tokens.spacingHorizontalS),
    flexWrap: "nowrap", // Keep Back/Continue horizontally aligned; labels shorten on narrow panes.
    width: "100%",
    justifyContent: "space-between",
  },
  footerButton: {
    minHeight: "44px",
    whiteSpace: "nowrap",
    textAlign: "center",
    minWidth: 0,
  },
  footerLabelLong: {
    display: "inline",
    "@media (max-width: 520px)": {
      display: "none",
    },
  },
  footerLabelShort: {
    display: "none",
    "@media (max-width: 520px)": {
      display: "inline",
    },
  },
});

export const FieldsSelectionPage: React.FC<{ onNavigate: (page: Page) => void }> = ({
  onNavigate,
}) => {
  const classes = useStyles();
  const { state, dispatch } = useJob();
  const { instance, accounts } = useMsal();
  const { selectedFields, customFields, customRule } = state.fieldConfig;

  const [categories, setCategories] = useState<UiFieldCategories>(FIELD_CATEGORIES);
  const [defaultFields, setDefaultFields] = useState<string[]>(DEFAULT_FIELDS);
  const [allowCustomFields, setAllowCustomFields] = useState<boolean>(true);
  const allCategories = useMemo(() => {
    if (allowCustomFields || customFields.length > 0) {
      return { ...categories, "Custom Fields": customFields };
    }
    return categories;
  }, [allowCustomFields, categories, customFields]);
  const [activeCategory, setActiveCategory] = useState(Object.keys(allCategories)[0]);
  const [customFieldInput, setCustomFieldInput] = useState("");
  const selectedFieldSet = useMemo(() => new Set(selectedFields), [selectedFields]);
  const activeCategoryFields = allCategories[activeCategory] ?? [];
  const selectedInActiveCategory = activeCategoryFields.filter((field) =>
    selectedFieldSet.has(field.value)
  ).length;

  useEffect(() => {
    const categoryKeys = Object.keys(allCategories);
    if (!categoryKeys.length) {
      return;
    }
    if (!categoryKeys.includes(activeCategory)) {
      setActiveCategory(categoryKeys[0]);
    }
  }, [activeCategory, allCategories]);

  useEffect(() => {
    const loadFieldProfile = async () => {
      if (!accounts.length) {
        return;
      }
      try {
        const accessToken = await getAccessToken(instance);
        const profile = await fetchFieldProfile(accessToken, "MEMBER_EXTRACTION");
        const normalized = normaliseFieldProfile(profile);
        if (Object.keys(normalized.categories).length > 0) {
          setCategories(normalized.categories);
        }
        if (normalized.defaultSelectedFields.length > 0) {
          setDefaultFields(normalized.defaultSelectedFields);
        }
        setAllowCustomFields(normalized.allowCustomFields);
      } catch (error) {
        // Keep local constants as a fallback so field selection remains usable.
        console.warn("Falling back to local member field catalogue.", error);
      }
    };
    loadFieldProfile().catch((error) =>
      console.error("Failed to load member field profile:", error)
    );
  }, [accounts, instance]);

  useEffect(() => {
    // On page entry, initialise selection from current defaults
    // (local defaults first, then profile defaults once loaded).
    dispatch({ type: "SET_SELECTED_FIELDS", payload: defaultFields });
  }, [dispatch, defaultFields]);

  const handleFieldChange = (fieldValue: string, isChecked: boolean) => {
    const newSelection = isChecked
      ? [...selectedFields, fieldValue]
      : selectedFields.filter((f) => f !== fieldValue);
    dispatch({ type: "SET_SELECTED_FIELDS", payload: newSelection });
  };

  const handleAddCustomField = () => {
    const label = customFieldInput.trim();
    if (!label) {
      return;
    }
    const value = label.toLowerCase().replace(/\s+/g, "_");
    const allFieldValues = [...Object.values(categories).flat(), ...customFields].map(
      (f) => f.value
    );
    if (allFieldValues.includes(value)) {
      console.warn("Field already exists:", value);
      return;
    }
    dispatch({ type: "ADD_CUSTOM_FIELD", payload: { value, label } });
    setCustomFieldInput("");
  };

  const handleRemoveCustomField = (fieldValue: string) => {
    dispatch({ type: "REMOVE_CUSTOM_FIELD", payload: fieldValue });
  };

  /** Select every field across ALL categories. */
  const handleSelectAll = () => {
    const allFieldValues = Object.values(allCategories)
      .flat()
      .map((field) => field.value);
    dispatch({ type: "SET_SELECTED_FIELDS", payload: allFieldValues });
  };

  /** Clear every field across ALL categories. */
  const handleClearAll = () => {
    dispatch({ type: "SET_SELECTED_FIELDS", payload: [] });
  };

  /** Restore the tenant profile's default selected fields. */
  const handleSelectDefaults = () => {
    dispatch({ type: "SET_SELECTED_FIELDS", payload: defaultFields });
  };

  /** Select only the fields visible in the current active tab. */
  const handleSelectTab = () => {
    const tabFields = (allCategories[activeCategory] ?? []).map((f) => f.value);
    // Merge tab fields into existing selection (avoid duplicates).
    const merged = Array.from(new Set([...selectedFields, ...tabFields]));
    dispatch({ type: "SET_SELECTED_FIELDS", payload: merged });
  };

  /** Un-check only the fields visible in the current active tab. */
  const handleClearTab = () => {
    const tabFieldValues = new Set((allCategories[activeCategory] ?? []).map((f) => f.value));
    // Remove only the current tab's fields, keep everything else selected.
    const remaining = selectedFields.filter((v) => !tabFieldValues.has(v));
    dispatch({ type: "SET_SELECTED_FIELDS", payload: remaining });
  };

  const renderFieldsForCategory = () => {
    const isCustom = activeCategory === "Custom Fields";
    const fields = (isCustom ? customFields : allCategories[activeCategory]) ?? [];

    return (
      <div>
        <div className={classes.categoryHeading}>
          <Text as="h3" weight="semibold" className={classes.sectionTitle}>
            {activeCategory}
          </Text>
          <Badge appearance="tint" color="brand">
            {selectedInActiveCategory} of {fields.length}
          </Badge>
        </div>

        {isCustom && allowCustomFields && (
          <div className={classes.customBuilder}>
            <Text as="h3" weight="semibold" className={classes.sectionTitle}>
              Add a custom field
            </Text>
            <Text as="p" size={300} className={classes.sectionDescription}>
              Create your own fields for extraction.
            </Text>
            <div className={classes.customFieldInput}>
              <Input
                value={customFieldInput}
                onChange={(e) => setCustomFieldInput(e.target.value)}
                placeholder="Enter new field name"
                style={{ flex: 1 }}
                onKeyDown={(e) => e.key === "Enter" && handleAddCustomField()}
              />
              <Button
                icon={<Add24Regular />}
                onClick={handleAddCustomField}
                appearance="primary"
                disabled={!customFieldInput}
              >
                Add
              </Button>
            </div>
          </div>
        )}
        <div className={classes.fieldGrid}>
          {fields.map((field) => (
            <div key={field.value} className={classes.fieldOption}>
              <Checkbox
                label={field.label}
                value={field.value}
                checked={selectedFieldSet.has(field.value)}
                onChange={(_e, data) => handleFieldChange(field.value, data.checked as boolean)}
              />
              {isCustom && (
                <Button
                  icon={<Delete24Regular />}
                  appearance="transparent"
                  size="small"
                  onClick={() => handleRemoveCustomField(field.value)}
                />
              )}
            </div>
          ))}
        </div>
        {isCustom && (
          <div className={classes.ruleSection}>
            <Text as="h3" weight="semibold" className={classes.sectionTitle}>
              Field extraction rule
            </Text>
            <Text as="p" size={300} className={classes.sectionDescription}>
              Provide specific instructions for data extraction.
            </Text>
            <Textarea
              value={customRule || ""}
              onChange={(e) => dispatch({ type: "SET_FIELD_CUSTOM_RULE", payload: e.target.value })}
              placeholder={
                allowCustomFields
                  ? "e.g., 'Extract the middle name only if explicitly provided.'"
                  : "Custom rules still apply to schema-listed fields."
              }
              style={{ width: "100%", minHeight: "96px" }}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.headerCard}>
        <div className={classes.headerEyebrow}>Member data extraction · Step 3</div>
        <Text as="h1" weight="bold" size={600} className={classes.headerTitle}>
          Choose report fields
        </Text>
        <Text as="p" size={300} className={classes.headerSubtitle}>
          Select the information to extract from each member document.
        </Text>
      </div>

      <Card className={classes.mainCard}>
        <div className={classes.selectionSummary}>
          <div className={classes.summaryIdentity}>
            <span className={classes.summaryIcon}>
              <DocumentTable24Regular />
            </span>
            <div>
              <Text weight="semibold" block>
                Extraction fields
              </Text>
              <Text size={200}>{activeCategory}</Text>
            </div>
          </div>
          <Badge appearance="filled" color="brand">
            {selectedFields.length} selected
          </Badge>
        </div>

        <div className={classes.selectionWorkspace}>
          <div className={classes.sidebar}>
            {Object.keys(allCategories).map((category) => (
              <Button
                key={category}
                appearance={activeCategory === category ? "primary" : "subtle"}
                className={classes.categoryBtn}
                onClick={() => setActiveCategory(category)}
              >
                <span className={classes.categoryLabel}>{category}</span>
              </Button>
            ))}
          </div>

          <div className={classes.mainContent}>
            <div className={classes.actionsBar}>
              <Button size="small" onClick={handleSelectDefaults}>
                Defaults
              </Button>
              <Button size="small" onClick={handleSelectTab}>
                Select category
              </Button>
              <Button size="small" onClick={handleClearTab}>
                Clear category
              </Button>
              <Button size="small" onClick={handleSelectAll}>
                Select all
              </Button>
              <Button size="small" onClick={handleClearAll}>
                Clear all
              </Button>
            </div>
            <div className={classes.contentScroll}>{renderFieldsForCategory()}</div>
          </div>
        </div>
      </Card>

      <div className={classes.footerCard}>
        <div className={classes.footerActions}>
          <Button
            appearance="primary"
            className={mergeClasses(classes.footerButton, "sourcestone-navigation-primary")}
            icon={<ArrowLeft24Regular />}
            onClick={() => onNavigate("memberSelection")}
          >
            <span className={classes.footerLabelLong}>Back to Member Selection</span>
            <span className={classes.footerLabelShort}>Back</span>
          </Button>
          <Button
            appearance="primary"
            className={mergeClasses(classes.footerButton, "sourcestone-navigation-primary")}
            icon={<ArrowRight24Regular />}
            iconPosition="after"
            onClick={() => onNavigate("confirmation")}
          >
            <span className={classes.footerLabelLong}>Continue to Confirmation</span>
            <span className={classes.footerLabelShort}>Continue</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
